import {
    LayoutDashboard,
    BarChart3,
    Bell,
    BookOpen,
    Upload,
    TrendingUp,
    ClipboardList,
    Heart,
    Users,
    GraduationCap,
    Trophy,
    Briefcase,
} from 'lucide-react'

import { INavItem } from './SideNav.types'

import { PERMISSIONS } from '@/features/authorization/constants/permissions'

export const baseNavItems: INavItem[] = [
    {
        id: 'documentation',
        label: 'Docs',
        icon: BookOpen,
        description: 'Support resources',
    },
]

export const hrNavItems: INavItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        description: 'Main HR overview',
        requiredPermissions: [PERMISSIONS.ANALYTICS.VIEW_OVERVIEW.code],
    },
    {
        id: 'people',
        label: 'People',
        icon: Users,
        description: 'Employee management',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.VIEW_ALL.code],
    },
    {
        id: 'alerts',
        label: 'Alerts',
        icon: Bell,
        badge: '3',
        description: 'Urgent notifications',
        requiredPermissions: [PERMISSIONS.ANALYTICS.VIEW_OVERVIEW.code], // General access for alerts
    },
    {
        id: 'reports',
        label: 'Executive Reports',
        icon: BarChart3,
        description: 'Organizational insights',
        requiredPermissions: [PERMISSIONS.ANALYTICS.VIEW_OVERVIEW.code, PERMISSIONS.ANALYTICS.CREATE_REPORTS.code],
        requiresAny: true, // User needs ANY of the permissions, not all
    },
    {
        id: 'surveys',
        label: 'Surveys',
        icon: ClipboardList,
        description: 'Pulse surveys & feedback',
        requiredPermissions: [PERMISSIONS.SURVEYS.VIEW.code],
    },
    {
        id: 'kudos',
        label: 'Kudos',
        icon: Heart,
        description: 'Recognition & appreciation',
        requiredPermissions: [PERMISSIONS.KUDOS.VIEW.code],
    },
    {
        id: 'upload',
        label: 'Uploads',
        icon: Upload,
        description: 'Upload HRIS data & records',
        requiredPermissions: [PERMISSIONS.HRIS_IMPORT.UPLOAD.code],
    },
]

export const managerNavItems: INavItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        description: 'Main team overview',
        requiredPermissions: [PERMISSIONS.ANALYTICS.VIEW_TEAM.code],
    },
    {
        id: 'team',
        label: 'Team',
        icon: Users,
        description: 'Team management',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.VIEW_TEAM.code],
    },
    {
        id: 'alerts',
        label: 'Alerts',
        icon: Bell,
        badge: '2',
        description: 'Team alerts & updates',
        requiredPermissions: [PERMISSIONS.ANALYTICS.VIEW_TEAM.code], // General team access for alerts
    },
    {
        id: 'reports',
        label: 'Reports',
        icon: TrendingUp,
        description: 'Team performance insights',
        requiredPermissions: [PERMISSIONS.ANALYTICS.VIEW_TEAM.code],
    },
    {
        id: 'surveys',
        label: 'Surveys',
        icon: ClipboardList,
        description: 'Team pulse surveys',
        requiredPermissions: [PERMISSIONS.SURVEYS.VIEW.code, PERMISSIONS.SURVEYS.VIEW_RESPONSES.code],
        requiresAny: true, // Manager can view surveys OR responses
    },
    {
        id: 'kudos',
        label: 'Kudos',
        icon: Heart,
        description: 'Send recognition & kudos',
        requiredPermissions: [PERMISSIONS.KUDOS.SEND.code, PERMISSIONS.KUDOS.VIEW.code],
        requiresAny: true, // Manager can send OR view kudos
    },
    {
        id: 'learn',
        label: 'Learn',
        icon: GraduationCap,
        description: 'Learning & development',
        // No specific permission - learning is generally available to managers
    },
    {
        id: 'upload',
        label: 'Uploads',
        icon: Upload,
        description: 'Upload reviews & feedback',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.MANAGE_DOCUMENTS.code], // Manager document management
    },
]

export const employeeNavItems: INavItem[] = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        description: 'Personal overview',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.VIEW_SELF.code],
    },
    {
        id: 'learn',
        label: 'Learn',
        icon: GraduationCap,
        description: 'Learning & development',
        // No specific permission - learning is generally available to all employees
    },
    {
        id: 'brag',
        label: 'Brag',
        icon: Trophy,
        description: 'Share achievements',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.EDIT_SELF.code], // Employee can edit their own profile for achievements
    },
    {
        id: 'career',
        label: 'Career',
        icon: Briefcase,
        description: 'Career development',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.VIEW_SELF.code], // Access to own career info
    },
    {
        id: 'upload',
        label: 'Uploads',
        icon: Upload,
        description: 'Upload documents',
        requiredPermissions: [PERMISSIONS.EMPLOYEE.MANAGE_DOCUMENTS.code], // Employee document upload
    },
]

export const getNavItems = (userType: 'hr' | 'manager' | 'employee'): INavItem[] => {
    switch (userType) {
        case 'hr':
            return [...hrNavItems, ...baseNavItems]
        case 'manager':
            return [...managerNavItems, ...baseNavItems]
        case 'employee':
            return [...employeeNavItems, ...baseNavItems]
        default:
            return baseNavItems
    }
}

/**
 * Filter nav items based on user permissions
 * @param navItems - Array of navigation items to filter
 * @param userPermissions - Array of permission codes the user has
 * @returns Filtered array of nav items the user can access
 */
export const filterNavItemsByPermissions = (navItems: INavItem[], userPermissions: string[]): INavItem[] => {
    return navItems.filter((item) => {
        // If no permissions are required, show the item
        if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
            return true
        }

        // Check if user has required permissions
        if (item.requiresAny) {
            // User needs ANY of the permissions
            return item.requiredPermissions.some((permission) => userPermissions.includes(permission))
        } else {
            // User needs ALL of the permissions (default behavior)
            return item.requiredPermissions.every((permission) => userPermissions.includes(permission))
        }
    })
}

/**
 * Get navigation items filtered by user permissions
 * @param userType - User role type
 * @param userPermissions - Array of permission codes the user has
 * @returns Permission-filtered navigation items
 */
export const getNavItemsWithPermissions = (
    userType: 'hr' | 'manager' | 'employee',
    userPermissions: string[],
): INavItem[] => {
    const allNavItems = getNavItems(userType)
    return filterNavItemsByPermissions(allNavItems, userPermissions)
}
